import React, { useEffect, useState } from 'react';

import {
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  ButtonGroup,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as Yup from 'yup';

import FormV2 from 'components/common/Form/FormV2/FormV2';
import { useToastContext } from 'components/common/ToastBannerProvider/ToastBannerProvider';
import { useOrgNavigate } from 'hooks/useOrgNavigate';
import { useNotificationPolicyItems } from 'queries/notificationPolicy';
import { useOnboardingStep } from 'queries/onboardingTasks';
import {
  useCreateSignalsNotification,
  useDeleteSignalsNotification,
  useSignalNotifications,
  useUpdateSignalsNotification,
} from 'queries/signalsNotifications';
// queries

// components

import {
  getUnmetPolicies,
  getPreferredDestinationForPolicy,
  getNotificationTypeForPolicy,
  getGroupComplianceStatus,
} from './helpers/complianceHelpers';
import {
  generateInitialValues,
  generateNotificationOptions,
  timeOptions,
} from './helpers/signalsNotificationFormHelpers';
import { NotificationRow } from './SignalNotificationRow';
import { NotificationEditRow } from './SignalsNotificationEditRow';
import { UnmetPolicyRow } from './UnmetPolicyRow';

const validationSchema = Yup.object().shape({
  notifications: Yup.array().of(
    Yup.object().shape({
      notification_type: Yup.object().required('Required'),
      delay: Yup.object().required('Required'),
    }),
  ),
});

// @param {Object} notificationPriority - Object containing the notification priority ("HIGH", "MEDIUM", "LOW")
// This component will only render notifications for the given notification priority set
const SignalsNotificationsForm = ({
  notificationPriority = 'HIGH',
  destinations = [],
  readOnly = false,
  refetchCompliance,
}) => {
  const { addToast } = useToastContext();

  // Calculate responsive column span for the "Add notification step" button
  const columnSpan = useBreakpointValue({ base: 4, md: 5 });
  const { hideWhatsappDestination } = useFlags();
  const {
    data: signalsNotifications,
    refetch,
    isLoading,
  } = useSignalNotifications({
    notificationPriority,
  });

  // Fetch policy items for compliance checking
  const { data: policyItems } = useNotificationPolicyItems();

  // Filters out notifications that are not of the given notification priority
  const filteredNotifications = signalsNotifications?.filter(
    (destination) => destination.notification_priority === notificationPriority,
  );

  // Calculate unmet policies
  const unmetPolicies = getUnmetPolicies(
    policyItems,
    filteredNotifications,
    notificationPriority,
  );

  const {
    isSignalsOnboarding,
    completeOnboardingStep,
    isStepComplete,
    isLoading: onboardingIsLoading,
  } = useOnboardingStep('configure_notification_preferences');
  const navigate = useOrgNavigate();
  const notificationOptions = generateNotificationOptions(destinations).filter(
    (option) => {
      if (hideWhatsappDestination && option.label === 'WhatsApp') {
        return false;
      }
      return true;
    },
  );

  // If you already have a notification setting and are onboarding, complete the step
  useEffect(() => {
    if (
      isSignalsOnboarding &&
      !onboardingIsLoading &&
      !isStepComplete &&
      filteredNotifications?.length > 0
    ) {
      completeOnboardingStep();
    }
  }, [isSignalsOnboarding, onboardingIsLoading, filteredNotifications]);

  const [editingIndex, setEditingIndex] = useState(null);

  const onSuccess = () => {
    console.log('SignalsNotificationForm: onSuccess - refetching compliance');
    if (isSignalsOnboarding && !isStepComplete) {
      completeOnboardingStep();
    }
    if (isSignalsOnboarding) {
      navigate('/signals/alerts');
    }
    addToast({
      message: 'Notification setting has been updated.',
      variant: 'success',
    });
    setEditingIndex(null);
    refetch();
    if (refetchCompliance) {
      refetchCompliance();
    } else {
      console.warn('SignalsNotificationForm: refetchCompliance not provided');
    }
  };

  const onDeleteSuccess = () => {
    console.log(
      'SignalsNotificationForm: onDeleteSuccess - refetching compliance',
    );
    addToast({
      message: 'Notification setting has been deleted.',
      variant: 'success',
    });
    setEditingIndex(null);
    refetch();
    if (refetchCompliance) {
      refetchCompliance();
    } else {
      console.warn('SignalsNotificationForm: refetchCompliance not provided');
    }
  };

  const onError = () => {
    addToast({
      message: 'Something went wrong updating notification settings.',
      variant: 'alert',
    });
  };

  const { mutate: createNotification } = useCreateSignalsNotification({
    onSuccess,
    onError,
  });
  const { mutate: updateNotification } = useUpdateSignalsNotification({
    onSuccess,
    onError,
  });
  const { mutate: deleteNotification } = useDeleteSignalsNotification({
    onSuccess: onDeleteSuccess,
    onError,
  });

  const onSubmit = (values) => {
    const submitValues = values.notifications.map((item) => {
      return {
        notification_type: item.notification_type.type,
        destination: item.notification_type.value,
        delay: item.delay.value,
        id: item.id,
        notification_priority: notificationPriority,
      };
    })[editingIndex];

    if (submitValues.id) {
      return updateNotification(submitValues);
    } else {
      return createNotification(submitValues);
    }
  };

  const deletePreference = (item, index, removeFunction) => {
    if (item.id) {
      deleteNotification(item);
    } else {
      removeFunction(index);
    }
  };

  const duplicatePreference = (item) => {
    console.log(
      'SignalsNotificationForm: duplicatePreference - creating duplicate notification',
    );
    const newDelayIndex =
      timeOptions.findIndex((option) => option.value === item.delay.value) + 1;
    const newDelay = timeOptions[newDelayIndex].value;
    const copy = {
      notification_type: item.notification_type.notification_type,
      destination: item.notification_type.value,
      // add 5 minutes to the iso8601 duration
      delay: newDelay,
      notification_priority: notificationPriority,
    };

    createNotification(copy);
  };

  const handleAddToSatisfyPolicy = (policy) => {
    console.log(
      'SignalsNotificationForm: handleAddToSatisfyPolicy - creating notification to satisfy policy',
      policy.id,
    );
    // Find a suitable destination based on the policy's notification group method
    const preferredDestination = getPreferredDestinationForPolicy(
      policy.notification_group_method,
      destinations,
    );

    if (!preferredDestination) {
      const destinationTypeNames = {
        email: 'email address',
        chat: 'Slack or Microsoft Teams',
        mobile_text: 'phone number',
        voice: 'phone number',
        apns: 'mobile device',
        fcm: 'mobile device',
      };

      const requiredType =
        destinationTypeNames[policy.notification_group_method] ||
        policy.notification_group_method;

      addToast({
        message: `No ${requiredType} destinations available. Please add one or verify in your Profile settings.`,
        variant: 'alert',
      });
      return;
    }

    // Determine the appropriate notification type based on the policy and destination
    let notificationType;
    if (policy.notification_group_method === 'chat') {
      // For chat policies, use the specific destination type
      notificationType =
        preferredDestination.type === 'slack' ? 'slack' : 'microsoft_teams';
    } else if (policy.notification_group_method === 'any') {
      // For 'any' policies, use the destination type directly
      notificationType = preferredDestination.type;
    } else {
      // For other policies, use the standard mapping with destination
      notificationType = getNotificationTypeForPolicy(
        policy.notification_group_method,
        preferredDestination,
      );
    }

    // Create the notification preference
    const newPreference = {
      notification_type: notificationType,
      destination: preferredDestination.value,
      delay: policy.max_delay, // Use the policy's max delay
      notification_priority: notificationPriority,
    };

    createNotification(newPreference);
  };

  const toggleEditState = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    } else {
      setEditingIndex(index);
    }
  };

  const cancelEdit = (resetForm) => {
    resetForm();
    toggleEditState(null);
  };

  const initialValues = generateInitialValues(filteredNotifications);

  return (
    <FormV2
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      requiredPermissions={['member', 'owner']}
      data-testid={`signals-notification-form-${notificationPriority}`}
      maxWidth="100%"
    >
      {(formik) => {
        const { groupIsTimeCompliant, priorityCompliance } =
          getGroupComplianceStatus(
            formik.values.notifications,
            policyItems,
            notificationPriority,
          );
        return (
          <FormV2.FieldArray name="notifications" ml="0">
            {(arrayHelpers) => {
              return (
                <TableContainer width="100%">
                  <Table variant="compressed" width="100%">
                    <Thead>
                      <Tr>
                        <Th fontSize={{ base: 'xs', md: 'sm' }}>Type</Th>
                        <Th fontSize={{ base: 'xs', md: 'sm' }}>Delay</Th>
                        <Th
                          fontSize={{ base: 'xs', md: 'sm' }}
                          width={{ base: '60px', md: 'auto' }}
                        >
                          In Policy
                        </Th>
                        <Th
                          display={{ base: 'none', md: 'table-cell' }}
                          fontSize={{ base: 'xs', md: 'sm' }}
                        >
                          Policy
                        </Th>
                        <Th fontSize={{ base: 'xs', md: 'sm' }}>
                          <Flex justifyContent="flex-end">Actions</Flex>
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <>
                        {/* Show unmet policies first */}
                        {unmetPolicies.map((policy) => (
                          <UnmetPolicyRow
                            key={`unmet-${policy.id}`}
                            policy={policy}
                            onAddToSatisfy={handleAddToSatisfyPolicy}
                            readOnly={readOnly}
                          />
                        ))}

                        {/* Show existing user preferences */}
                        {arrayHelpers.form.values.notifications.map(
                          (item, index) => {
                            const rowCompliance = priorityCompliance[item.id];
                            const complianceFulfilledByGroup =
                              rowCompliance.isCompliant === false &&
                              groupIsTimeCompliant;
                            if (complianceFulfilledByGroup) {
                              rowCompliance.isCompliant = true;
                              rowCompliance.complianceFulfilledByGroup = true;
                            }
                            return editingIndex === index ? (
                              <NotificationEditRow
                                key={index}
                                index={index}
                                notificationOptions={notificationOptions}
                                item={item}
                                formik={formik}
                                cancelEdit={cancelEdit}
                                policyItems={policyItems}
                                notificationPriority={notificationPriority}
                                complianceStatus={rowCompliance}
                              />
                            ) : (
                              <NotificationRow
                                key={index}
                                index={index}
                                item={item}
                                formik={formik}
                                toggleEditState={toggleEditState}
                                duplicatePreference={duplicatePreference}
                                deletePreference={deletePreference}
                                arrayHelpers={arrayHelpers}
                                isLoading={isLoading}
                                readOnly={readOnly}
                                complianceStatus={rowCompliance}
                              />
                            );
                          },
                        )}
                        {readOnly ? null : (
                          <Tr
                            borderBottom="none"
                            _hover={{ bg: 'surface.primary' }}
                          >
                            <Td
                              colSpan={columnSpan}
                              borderBottom="none"
                              textAlign="left"
                            >
                              <ButtonGroup>
                                <FormV2.ArrayAddButton
                                  buttonText="Add step"
                                  buttonProps={{
                                    width: '100%',
                                    variant: 'ghost',
                                  }}
                                  addAction={() => {
                                    const lastElement =
                                      arrayHelpers.form.values.notifications[
                                        arrayHelpers.form.values.notifications
                                          .length - 1
                                      ];

                                    if (
                                      lastElement &&
                                      (lastElement.notification_type === '' ||
                                        lastElement.delay === '')
                                    ) {
                                      addToast({
                                        message:
                                          'Please complete the previous notification before adding another.',
                                        variant: 'info',
                                      });
                                    } else {
                                      if (editingIndex !== null) {
                                        formik.handleSubmit();
                                      } else {
                                        arrayHelpers.push({
                                          notification_type: '',
                                          delay: '',
                                        });
                                        setEditingIndex(
                                          arrayHelpers.form.values.notifications
                                            .length,
                                        );
                                      }
                                    }
                                  }}
                                />
                              </ButtonGroup>
                            </Td>
                          </Tr>
                        )}
                      </>
                    </Tbody>
                  </Table>
                </TableContainer>
              );
            }}
          </FormV2.FieldArray>
        );
      }}
    </FormV2>
  );
};

export default SignalsNotificationsForm;
